import { useEffect, useState } from 'react';
import { CheckCircle, KeyboardArrowLeft } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  FormGroup,
  Grid,
  Stack,
  TextField,
  MenuItem,
} from '@mui/material';
import {
  FieldValues,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';

import Form from 'components/Form';
import { getSignupCountries } from 'apis/account';
import { ISignupCountryOption } from 'apis/account/type';
import { getCollegeList } from 'apis/admin';
import {
  BIRTH_DECADE_DEFAULT,
  BIRTH_DECADE_NONE_LABEL,
  BIRTH_DECADE_OPTIONS,
  isSignupAllCountries,
  isSignupCountryChosen,
  SIGNUP_ALL_COUNTRY_ID,
} from 'constants/signup';
import MuiAutocomplete from './components/MuiAutocomplete';

import { ISendMailProps, IVerifyMailProps } from './types';
import SignUpDialog from './SignUpDialog';
import { styled } from '@mui/styles';

interface Props {
  register: UseFormRegister<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  collegeCountryId?: number;
  sendMailProps: ISendMailProps;
  verifyMailProps: IVerifyMailProps;
  rcCode: number | null;
  isSubmitting?: boolean;
}

function SignUpForm(props: Props) {
  const [countryOptions, setCountryOptions] = useState<ISignupCountryOption[]>(
    []
  );
  const [isTerms, setIsTerms] = useState(false);
  const [isPersonal, setIsPersonal] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(0);
  const [isEmailButtonDisabled, setIsEmailButtonDisabled] = useState(false);

  const sxBtnStyles = { width: 100, pl: 0, pr: 0, fontSize: 16 };
  const {
    register,
    setValue,
    getValues,
    sendMailProps,
    verifyMailProps,
    rcCode,
    isSubmitting = false,
    collegeCountryId,
  } = props;

  useEffect(() => {
    getSignupCountries().then(({ data }) => {
      if (data.rc === 1) {
        setCountryOptions(data.countries ?? []);
      }
    });
  }, []);

  const VerificationIcon = () => (
    <CheckCircle
      fontSize="small"
      color="primary"
      sx={{
        display: verifyMailProps.isVerification ? 'block' : 'none',
      }}
    />
  );

  // 출생년대 옵션 — 「선택 안 함」은 빈 값으로 두고 전송 시 null 로 바꾼다
  const rendering = () => [
    <StMenuItem key="none" value="">
      {BIRTH_DECADE_NONE_LABEL}
    </StMenuItem>,
    ...BIRTH_DECADE_OPTIONS.map(decade => (
      <StMenuItem key={decade} value={decade}>
        {decade}
      </StMenuItem>
    )),
  ];

  // select option style
  const StMenuItem = styled(MenuItem)({
    height: 55,
  });

  // 비밀번호 에러 찾기
  const [passwordValue, setPasswordValue] = useState('');

  const passwordRegex = () => {
    let check =
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&^~\-\+\\()_=|])[A-Za-z\d@$!%*#?&^~\-\+\\()_=|]{8,}$/i;
    return check.test(passwordValue);
  };

  const [passwordCheck, setPasswordCheck] = useState('');

  // 이름에 특수문자 넣지못하게 막는
  const [이름, set이름] = useState('');

  const nameRegex = () => {
    let check = /@/;
    return check.test(이름);
  };

  // 이메일 유효성
  const [이메일, set이메일] = useState('');

  const emailRegex = () => {
    let check = /^([a-z0-9_\.-]+)@([\da-z\.-]+)\.([a-z\.]{2,10})$/;

    return check.test(이메일);
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (rcCode === 1 && cooldownTime > 0) {
      setIsEmailButtonDisabled(true);
      timer = setInterval(() => {
        setCooldownTime(prev => prev - 1);
      }, 1000);
    } else if (rcCode === 108) {
      setCooldownTime(0);
      setIsEmailButtonDisabled(false);
    } else {
      setIsEmailButtonDisabled(false);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [cooldownTime, rcCode]);

  const handleEmailSend = () => {
    sendMailProps.onClick();
    setCooldownTime(60);
  };

  const getEmailHelperText = () => {
    if (!emailRegex()) {
      return '본인의 대학교 이메일을 입력해주세요(없을시 개인 이메일을 입력해주세요)';
    }
    if (cooldownTime > 0 && sendMailProps.isSendMail) {
      return '인증번호는 1분 후 재전송 가능합니다.';
    }
    return '';
  };
  return (
    <Box>
      <Container maxWidth="sm" sx={{ mt: 7.5, mb: 6 }}>
        <Button
          href="/#/"
          size="large"
          color="inherit"
          startIcon={<KeyboardArrowLeft />}
          sx={{ mb: 5, p: 0 }}
        >
          회원가입
        </Button>
        <Stack spacing={2.5}>
          <Form.Item label="이메일">
            <Box display="flex" gap={1}>
              <TextField
                helperText={getEmailHelperText()}
                color={emailRegex() ? 'success' : 'warning'}
                required
                fullWidth
                type="email"
                {...register('userEmail', {
                  onChange: e => set이메일(e.target.value),
                })}
              />
              <LoadingButton
                sx={{ ...sxBtnStyles, maxHeight: 56 }}
                loading={sendMailProps.isLoading}
                variant={sendMailProps.isSendMail ? 'outlined' : 'contained'}
                onClick={handleEmailSend}
                disabled={isEmailButtonDisabled}
              >
                {sendMailProps.isSendMail
                  ? cooldownTime > 0
                    ? `${cooldownTime}초`
                    : '재전송'
                  : '인증하기'}
              </LoadingButton>
            </Box>
          </Form.Item>
          <Form.Item label="인증번호" isHidden={!sendMailProps.isSendMail}>
            <Box display="flex" gap={1}>
              <TextField
                required
                fullWidth
                placeholder="이메일로 전송된 인증번호를 입력해주세요."
                helperText="인증번호 메일 수신이 되지 않을 경우 스팸메일함 확인을 부탁드립니다."
                InputProps={{
                  readOnly: verifyMailProps.isVerification,
                  endAdornment: <VerificationIcon />,
                }}
                {...register('userCode')}
              />
              <LoadingButton
                variant="contained"
                loading={verifyMailProps.isLoading}
                onClick={verifyMailProps.onClick}
                sx={{
                  ...sxBtnStyles,
                  display: !verifyMailProps.isVerification ? 'block' : 'none',
                  height: '55px',
                }}
              >
                확인
              </LoadingButton>
            </Box>
          </Form.Item>
          <Form.Item label="비밀번호">
            <Form.Password
              required
              fullWidth
              placeholder="영문 + 숫자 + 특수기호를 포함해서 8자리 이상 입력해 주세요."
              helperText={
                passwordRegex()
                  ? '안전합니다!'
                  : '특수문자는 @ $ ! % * # ^ () _ = | ] 만 가능합니다.'
              }
              color={passwordRegex() ? 'success' : 'warning'}
              {...register('userPassword', {
                onChange: e => setPasswordValue(e.target.value),
              })}
            />
          </Form.Item>
          <Form.Item label="비밀번호 확인">
            <Form.Password
              required
              fullWidth
              isHideVisibleBtn
              helperText={
                passwordValue === passwordCheck
                  ? '일치합니다!'
                  : '일치하지않습니다!'
              }
              color={passwordValue === passwordCheck ? 'success' : 'warning'}
              {...register('userPasswordConfirm', {
                onChange: e => setPasswordCheck(e.target.value),
              })}
            />
          </Form.Item>
          <Form.Item label="이름">
            <TextField
              helperText={
                nameRegex()
                  ? '이메일 주소를 작성하지 않도록 주의해 주세요.'
                  : ''
              }
              color={nameRegex() ? 'warning' : 'success'}
              required
              fullWidth
              {...register('userName', {
                onChange: e => set이름(e.target.value),
              })}
              autoComplete="off"
            />
          </Form.Item>
          <Grid container>
            <Grid item xs={6}>
              <Form.Item label="성별">
                <Form.MuiRadioGroup
                  i18nKey="GENDER"
                  values={[1, 2]}
                  defaultValue={getValues('gender')}
                  onChange={value => setValue('gender', value)}
                />
              </Form.Item>
            </Grid>
            <Grid item xs={6}>
              <Form.Item label="구분">
                <Form.MuiRadioGroup
                  i18nKey="GRADE"
                  values={[1, 2]}
                  defaultValue={getValues('grade')}
                  onChange={value => setValue('grade', value)}
                />
              </Form.Item>
            </Grid>
          </Grid>

          <Form.Item label="국적 선택">
            <TextField
              select
              fullWidth
              required
              variant="outlined"
              defaultValue={SIGNUP_ALL_COUNTRY_ID}
              InputLabelProps={{ shrink: true }}
              {...register('country')}
            >
              <StMenuItem value="" disabled>
                국적 선택
              </StMenuItem>
              <StMenuItem value={SIGNUP_ALL_COUNTRY_ID}>전체</StMenuItem>
              {countryOptions.map(({ college_country_id, college_country_name }) => (
                <StMenuItem key={college_country_id} value={college_country_id}>
                  {college_country_name}
                </StMenuItem>
              ))}
            </TextField>
          </Form.Item>

          <Form.Item label="학교 선택">
            <MuiAutocomplete
              key={collegeCountryId ?? 'no-country'}
              listKey="college_lists"
              valueKey="college_id"
              collegeCountryId={collegeCountryId}
              noOptionsText={
                !isSignupCountryChosen(collegeCountryId)
                  ? '국적을 먼저 선택해주세요'
                  : '다른 학교를 입력해주세요'
              }
              placeholder={
                isSignupCountryChosen(collegeCountryId)
                  ? isSignupAllCountries(collegeCountryId)
                    ? '전체 학교 검색'
                    : '학교 검색'
                  : '국적 선택 후 검색'
              }
              onChange={value => setValue('college', value?.college_id)}
              getApi={getCollegeList}
              getOptionLabel={option => option.college_name}
              helperText="학교이름을 직접 입력하세요"
            />
          </Form.Item>
          <Form.Item label="학번/사번">
            <TextField
              required
              fullWidth
              variant="outlined"
              helperText="중복 학번/사번은 가입이 안됩니다."
              {...register('studentNo')}
            />
          </Form.Item>
          <Form.Item label="출생년대(*선택사항)">
            <TextField
              fullWidth
              select
              InputLabelProps={{ shrink: true }}
              defaultValue={BIRTH_DECADE_DEFAULT}
              {...register('birth')}
              SelectProps={{
                displayEmpty: true,
                MenuProps: { sx: { maxHeight: 400 } },
              }}
            >
              {rendering()}
            </TextField>
          </Form.Item>
        </Stack>

        <FormGroup sx={{ mt: 2.5 }}>
          <FormControlLabel
            label="(필수) 스마트널스 서비스 이용약관 동의"
            control={
              <Checkbox
                required
                size="small"
                onChange={(_, checked) => setIsTerms(checked)}
              />
            }
          />
          <FormControlLabel
            label="(필수) 개인정보 수집 및 이용 동의"
            control={
              <Checkbox
                required
                size="small"
                onChange={(_, checked) => setIsPersonal(checked)}
              />
            }
          />
        </FormGroup>

        <Button
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          sx={{ mt: 3.25, mb: '100px' }}
          disabled={isSubmitting}
        >
          {isSubmitting ? '가입 처리 중...' : '가입 신청하기'}
        </Button>
      </Container>

      <SignUpDialog.TermsOfService
        isOpen={isTerms}
        onClose={() => setIsTerms(false)}
      />

      <SignUpDialog.PersonalInfo
        isOpen={isPersonal}
        onClose={() => setIsPersonal(false)}
      />
    </Box>
  );
}

export default SignUpForm;
